import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { GetUserMessagesOpts } from '../../../interfaces/message';
import { environment } from '../../../../environments/environment';
import { Message } from '../../../models/message';
import { MessageControllerService } from '../../../services/message-controller.service';
import { BusinessEntity } from '../../../models/business-entity';
import { PaginationService } from 'ngx-pagination';
import { GlobalStateProviderService } from '../../../services/global-state-provider.service';
import { Subscription } from 'rxjs';
import { WebsocketService } from '../../../services/websocket.service';
import { WebSocketMessageType } from '../../../enums';
import { PopoverDirective } from 'ngx-bootstrap/popover';

@Component({
  selector: 'app-message-list',
  templateUrl: './message-list.component.html'
})
export class MessageListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() businessEntity: BusinessEntity | null = null;
  @Output() updateTotalMessageCountEmitter = new EventEmitter<number>();
  @Output() updateUnSeenMessageCountEmitter = new EventEmitter<number>();
  DATETIME_FORMAT: string = environment.DATETIME_FORMAT;
  messageList: Message[] = [];
  currentMessagePage: number = 1;
  searchPhrase: string = '';
  currentMessagesCount: number = 0;
  totalMessagesCount: number = 0;
  institutionsRequested: boolean = false;
  refreshInterval: ReturnType<typeof setInterval> | null = null;
  private subscription?: Subscription;
  private readonly popoverMap = new Map<any, PopoverDirective>();

  constructor(
    public messageController: MessageControllerService,
    public paginationService: PaginationService,
    public globalStateProviderService: GlobalStateProviderService,
    private readonly webSocket: WebsocketService
  ) {}

  ngOnInit(): void {
    this.getUserMessages();
    this.recalcMessagesCount();
    this.subscription = this.webSocket.messageReceived$.subscribe((message) => {
      switch (message?.type) {
        case WebSocketMessageType.NEW_MESSAGE:
        case WebSocketMessageType.NEW_ALERT_MESSAGE:
          this.messageList.unshift({
            ...new Message(),
            ...message.data?.message
          });
          this.recalcMessagesCount();
          break;
      }
    });
  }

  ngOnDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getUserMessages() {
    let opts: GetUserMessagesOpts = {};
    if (this.searchPhrase?.trim()) {
      opts.search_phrase = this.searchPhrase.trim();
    }
    if (this.businessEntity) {
      opts.business_entity = this.businessEntity.business_entity_id;
    }

    opts.page = this.currentMessagePage;

    this.messageController.getUserMessages(opts).subscribe((result) => {
      this.currentMessagesCount = result.count;
      this.messageList = result.results;
      if (this.messageList.length) {
        this.requestInstitutions(() => this.enrichMessagesWithInstitutions());
      }
    });
  }

  private enrichMessagesWithInstitutions(): void {
    this.messageList.forEach((message) => {
      const match = this.globalStateProviderService.institutions.find(
        (inst) => inst.id.toString() === message.message_from
      );
      if (match) {
        message.message_from_institution_name = match.shortName || match.name;
      }
    });
  }

  requestInstitutions(cb: any) {
    if (this.institutionsRequested) return cb();
    if (this.globalStateProviderService.institutions.length) return cb(); // requested somewhere else already
    this.institutionsRequested = true;

    this.messageController.getInstitutions().subscribe((institutions) => {
      this.globalStateProviderService.institutions = institutions ?? [];
      return cb();
    });
  }

  onSearchPhraseChange() {
    this.currentMessagePage = 1;
    this.getUserMessages();
  }

  onMessagePageChange(page: number) {
    this.currentMessagePage = page;
    this.getUserMessages();
  }

  toggleMessage(message: Message) {
    message.isOpen = !message.isOpen;
    message.marked = true;
    if (message.isOpen && !message.is_seen) {
      this.markMessageAsRead(message);
    }
  }

  ngOnChanges(): void {
    this.currentMessagePage = 1;
    this.getUserMessages();
  }

  markMessageAsRead(message: Message) {
    this.messageController.markMessageAsRead(message).subscribe(() => {
      message.is_seen = true;
      this.recalcMessagesCount();
      this.messageController.getAlerts().subscribe((messages) => {
        this.globalStateProviderService.alerts = messages;
      });
    });
  }

  recalcMessagesCount() {
    this.messageController.getTotalMessagesCount().subscribe((num) => {
      this.totalMessagesCount = num;
      this.updateTotalMessageCountEmitter.emit(num);
    });
    this.messageController.getUnSeenCount().subscribe((num) => {
      this.updateUnSeenMessageCountEmitter.emit(num);
    });
  }

  resetFilters() {
    this.searchPhrase = '';
    this.onSearchPhraseChange();
  }

  registerPopover(messageId: any, popover: PopoverDirective): void {
    this.popoverMap.set(messageId, popover);
  }

  hidePopover(messageId: string): void {
    const popover = this.popoverMap.get(messageId);
    if (popover) {
      popover.hide();
    } else {
      console.error('Could not find popover for messageId', messageId);
    }
  }

  deleteMessage(message: Message) {
    this.messageController.deleteMessage(message).subscribe(() => {
      /* Below is to make sure that we navigate to previous page
         if we deleted final result on currently last page */
      if (this.messageList.length <= 1) {
        this.currentMessagePage = Math.max(1, this.currentMessagePage - 1);
      }
      this.getUserMessages();
      this.recalcMessagesCount();
    });
  }

  protected readonly environment = environment;
}
